import { getToken } from "./auth";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getToken();
  const res = await fetch(path, {
    method,
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { "content-type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: { error?: string; code?: string } | null = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // Not from the platform (e.g. the firewall or load balancer answered).
    throw new ApiError(res.status, res.status === 403 ? "Blocked by the security firewall (403). Tell your administrator what you were doing." : `The server could not be reached (${res.status}). Try again in a minute.`);
  }
  if (!res.ok) throw new ApiError(res.status, data?.error ?? `Request failed (${res.status})`, data?.code);
  return data as T;
}

export const api = {
  get: <T>(p: string) => request<T>("GET", p),
  post: <T>(p: string, body: unknown = {}) => request<T>("POST", p, body),
  patch: <T>(p: string, body: unknown) => request<T>("PATCH", p, body),
  put: <T>(p: string, body: unknown) => request<T>("PUT", p, body),
  del: <T>(p: string) => request<T>("DELETE", p),
};

/** Fetch an authenticated binary (PDF, audio) and hand it to the browser. */
export async function downloadAuthed(path: string, filename: string): Promise<void> {
  const token = await getToken();
  const res = await fetch(path, { headers: token ? { authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new ApiError(res.status, "Download failed");
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export async function blobUrlAuthed(path: string): Promise<string> {
  const token = await getToken();
  const res = await fetch(path, { headers: token ? { authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new ApiError(res.status, "Could not load the recording");
  return URL.createObjectURL(await res.blob());
}

export const t = (tenant: string) => `/api/t/${encodeURIComponent(tenant)}`;
